import { FunctionComponent, useMemo, useEffect, useRef } from 'react';
import { GridApi, GridOptions, SortChangedEvent, GridReadyEvent, ColDef } from 'ag-grid-community';

import { PoGrid, BASE_GRID_OPTIONS } from '@/components/grid/PoGrid';
import { TransactionModel } from '../types/TransactionModel';
import { useGridColumnState } from '@/hooks/useGridColumnState';
import { UserSettingKey } from '@/modules/users/api/user-settings/user-setting.contracts';
import { ColumnID } from '@/components/grid/column-ids';
import { DateCellRenderer } from '@/components/grid/cells/DateCellRenderer';
import { RelativeDateCellRenderer } from '@/components/grid/cells/RelativeDateCellRenderer';
import { useTranslation } from '@/lib';
import { TransactionTypeCellRenderer } from './grid-cells/TransactionTypeCellRenderer';
import { GuestTransactionStatusCellRenderer } from './grid-cells/GuestTransactionStatusCellRenderer';

interface GuestTransactionsGridProps {
  data: TransactionModel[] | undefined;
  isLoading: boolean;
  onRowDoubleClicked: (transactionId: string) => void;
  onSortChanged: (event: SortChangedEvent<TransactionModel>) => void;
  onGridReady: (event: GridReadyEvent<TransactionModel>) => void;
  onExportToExcelClicked: (gridApi: GridApi<TransactionModel> | undefined) => void;
  onRefresh?: () => void;
}

export const GuestTransactionsGrid: FunctionComponent<GuestTransactionsGridProps> = ({
  data,
  isLoading,
  onRowDoubleClicked,
  onSortChanged,
  onGridReady,
  onExportToExcelClicked,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const gridApiRef = useRef<GridApi<TransactionModel>>();

  // Initialize the grid column state hook
  const {
    setColumnStateGridApi,
    handleColumnStateChange,
    columnState,
    applyStateToDefinitions,
    setIsAutoSaveEnabled,
    setDefaultColumnState,
  } = useGridColumnState(UserSettingKey.TRANSACTION_OVERVIEW_GRID_COLUMN_STATE);

  // Enable auto-saving of column state
  useEffect(() => {
    setIsAutoSaveEnabled(true);
  }, [setIsAutoSaveEnabled]);

  const columns: ColDef<TransactionModel>[] = useMemo(() => {
    const baseColumns: ColDef<TransactionModel>[] = [
      {
        colId: ColumnID.TRANSACTION_ID,
        field: 'transactionId',
        headerName: t('transaction_id'),
        cellClass: 'cursor-pointer hover:text-primary-500',
        onCellClicked: (params) => {
          if (params.data) {
            onRowDoubleClicked(params.data.id.toString());
          }
        },
        sortable: true,
      },
      {
        colId: ColumnID.TRANSACTION_TYPE,
        field: 'type',
        headerName: t('type'),
        cellRenderer: TransactionTypeCellRenderer,
        sortable: true,
      },
      {
        colId: ColumnID.TRANSACTION_STATUS,
        field: 'status',
        headerName: t('status'),
        cellRenderer: GuestTransactionStatusCellRenderer,
        sortable: true,
      },
      {
        colId: ColumnID.TRANSACTION_FROM_LOCATION,
        field: 'fromLocation',
        headerName: t('from_location'),
        valueGetter: (params) => params.data?.fromLocation.name,
        sortable: true,
      },
      {
        colId: ColumnID.TRANSACTION_TO_LOCATION,
        field: 'toLocation',
        headerName: t('to_location'),
        valueGetter: (params) => params.data?.toLocation.name,
        sortable: true,
      },
      {
        colId: ColumnID.TRANSACTION_CARRIER,
        field: 'carrier',
        headerName: t('carrier'),
        valueGetter: (params) => params.data?.carrier?.name,
        sortable: false,
      },
      {
        colId: ColumnID.TRANSACTION_DATE,
        field: 'date',
        headerName: t('date'),
        cellRenderer: DateCellRenderer,
        valueFormatter: (params) => (params.value ? new Date(params.value).toISOString() : ''),
        sortable: true,
      },
      {
        colId: ColumnID.TRANSACTION_DOCUMENT_NUMBER,
        field: 'documentNumber',
        headerName: t('document_number'),
        sortable: false,
      },
      {
        colId: ColumnID.TRANSACTION_REFERENCE_NUMBER,
        field: 'referenceNumber',
        headerName: t('reference_number'),
        sortable: false,
      },
      {
        colId: ColumnID.CREATED_AT,
        field: 'createdAt',
        headerName: t('created_at'),
        cellRenderer: RelativeDateCellRenderer,
        valueFormatter: (params) => (params.value ? new Date(params.value).toISOString() : ''),
      },
      {
        colId: ColumnID.CREATED_BY,
        field: 'createdBy',
        headerName: t('created_by'),
        valueGetter: (params) => params.data?.createdBy?.name || '-',
        sortable: false,
      },
    ];

    // Extract unique asset types from transaction data and sort alphabetically
    const uniqueAssetTypes = new Map<number, { id: number; name: string; code: string }>();

    if (data) {
      data.forEach((transaction) => {
        transaction.details.forEach((detail) => {
          if (!uniqueAssetTypes.has(detail.assetType.id)) {
            uniqueAssetTypes.set(detail.assetType.id, detail.assetType);
          }
        });
      });
    }

    // Convert to array and sort alphabetically by name
    const sortedAssetTypes = Array.from(uniqueAssetTypes.values()).sort((a, b) => a.name.localeCompare(b.name));

    // Generate dynamic columns for each asset type
    const assetTypeColumns: ColDef<TransactionModel>[] = sortedAssetTypes.map((assetType, index) => ({
      colId: `asset-type-${assetType.id}`,
      headerName: assetType.name,
      headerTooltip: assetType.name,
      valueGetter: (params) => {
        if (!params.data) return null;
        const detail = params.data.details.find((d) => d.assetType.id === assetType.id);
        return detail ? detail.amount : null;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return '';
        return params.value.toString();
      },
      sortable: false,
      hide: index >= 2, // Hide all columns after the first 2
    }));

    // Combine base columns with asset type columns
    const allColumns = [...baseColumns, ...assetTypeColumns];

    // Update default column state to include asset type columns
    const defaultState = [
      { colId: ColumnID.TRANSACTION_ID, hide: false },
      { colId: ColumnID.TRANSACTION_TYPE, hide: false },
      { colId: ColumnID.TRANSACTION_STATUS, hide: false },
      { colId: ColumnID.TRANSACTION_FROM_LOCATION, hide: false },
      { colId: ColumnID.TRANSACTION_TO_LOCATION, hide: false },
      { colId: ColumnID.TRANSACTION_CARRIER, hide: false },
      { colId: ColumnID.TRANSACTION_DATE, hide: false },
      { colId: ColumnID.TRANSACTION_DOCUMENT_NUMBER, hide: false },
      { colId: ColumnID.TRANSACTION_REFERENCE_NUMBER, hide: false },
      { colId: ColumnID.CREATED_AT, hide: false },
      { colId: ColumnID.CREATED_BY, hide: false },
      ...sortedAssetTypes.map((assetType, index) => ({
        colId: `asset-type-${assetType.id}`,
        hide: index >= 2, // Hide all asset type columns after the first 2
      })),
    ];

    if (columnState && columnState.length !== allColumns.length) {
      setDefaultColumnState(defaultState);
    }

    // Apply saved column state to the column definitions
    if (data) {
      applyStateToDefinitions(allColumns);
    }

    return allColumns;
  }, [t, columnState, data, applyStateToDefinitions, onRowDoubleClicked, setDefaultColumnState]);

  const gridOptions: GridOptions<TransactionModel> = useMemo(
    () => ({
      ...BASE_GRID_OPTIONS,
      rowData: data,
      rowSelection: undefined, // Disable row selection for guests
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: t('columns'),
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressColumnFilter: false,
              suppressColumnSelectAll: false,
              suppressColumnExpandAll: false,
            },
          },
        ],
        defaultToolPanel: '',
      },
      onSortChanged: (event) => {
        if (event.columns && event.columns[0] && event.source === 'uiColumnSorted') {
          event.api.applyColumnState({
            state: [
              {
                colId: event.columns[0].getColId(),
                sort: event.columns[0].getSort(),
              },
            ],
            defaultState: { sort: null },
          });
        }
        handleColumnStateChange(event);
        onSortChanged(event);
      },
      onGridReady: (event) => {
        gridApiRef.current = event.api;
        setColumnStateGridApi(event.api);
        onGridReady(event);
      },
      onColumnMoved: handleColumnStateChange,
      onColumnPinned: handleColumnStateChange,
      onColumnVisible: handleColumnStateChange,
      onColumnResized: handleColumnStateChange,
      onRowDoubleClicked: (event) => {
        if (event.data) {
          onRowDoubleClicked(event.data.id.toString());
        }
      },
      getContextMenuItems: (params) => [
        'copy',
        'separator',
        {
          name: t('export_excel'),
          action: () => onExportToExcelClicked(params.api),
        },
      ],
      getRowId: (params) => params.data.id.toString(),
      context: {
        onStatusUpdated: () => {
          // Trigger a full grid refresh with API call
          onRefresh?.();
        },
      },
    }),
    [
      data,
      isLoading,
      onSortChanged,
      onGridReady,
      handleColumnStateChange,
      onRowDoubleClicked,
      setColumnStateGridApi,
      t,
      onExportToExcelClicked,
      onRefresh,
    ],
  );

  return (
    <div className="h-full w-full">
      <PoGrid isLoading={isLoading} colDefs={columns} gridOptions={gridOptions} rowData={data} disableDefaultGridOptions />
    </div>
  );
};
